import moment from "moment";
import { List } from "immutable";
import debounce from "lodash/debounce";
import { fromJS, isImmutable } from "immutable";
import { initialize } from "redux-form/immutable";
import { push } from "connected-react-router/immutable";

import { toggleModal } from "altus-modal";
import { openInWindow } from "altus-ui-components";

import {
  NOTIFICATION_VARIANTS,
  addNotification,
} from "altus-redux-middlewares";

import {
  updateEmployee,
  queryEmployeeWorkSchedule,
} from "features/planner/employee/employee.actions";

import {
  APP_ACTIONS,
  EMPTY_LIST,
  EMPTY_SET,
  EMPTY_MAP,
} from "app/app.constants";

import {
  getFileBase64,
  invokeIfFunction,
  makeActionCreator,
} from "utils/app.util";

import {
  CompanyMapper,
  shiftTypeToString,
} from "features/planner/planner.mappers";

import {
  EMPLOYEE_CARD_HASH_TARGET,
  DEPLOYMENT_DETAILS_MODAL_ID,
} from "features/planner/employee/employee.constants";

import {
  isDeploymentOffshore,
  isReturnOnshore,
  isShuttle,
  isAllocated,
} from "utils/planner.util";

import {
  ACTIONS,
  CREATE_VIEW_GROUPS_FORM,
  COMPANY_FORM,
  CARD_FORM,
  CREATE_CARD_MODAL_ID,
  CREATE_CARD_MOVE_FORM,
  CREATE_VIEW_FORM,
  CREW_FORM,
  DEPLOY_OFFSHORE_MODAL_ID,
  EDIT_CARD_MODAL_ID,
  EDIT_FIXED_FACILITY_FORM,
  EMPLOYEE_GROUP_FORM,
  FIELD_FORM,
  GROUP_FORM,
  GROUP_VISUAL_STATE,
  MARKER_FORM,
  MOBILE_FACILITY_FORM,
  QUALIFICATION_FORM,
  RETURN_ONSHORE_MODAL_ID,
  SHIFT_TYPES,
  SHUTTLE_OFFSHORE_MODAL_ID,
  VIEW_TYPES,
  SUB_CONTRACTOR_FORM,
  CREATE_CARD_TYPE,
  SUB_CONTRACTOR_COMPANY_FORM,
  DEPLOYMENT_DETAILS_FORM,
  CARD_TYPES,
  MISSING_COMPETENCE_MODAL_ID,
  CREATE_VIEW_GROUPS_TABS,
} from "features/planner/planner.constants";

import {
  getCardContainerFromState,
  getCardFromState,
  getGroupFromState,
  getViewGroupByGroupIdFromState,
  getViewGroupFromState,
  getWebSocketDidErrorFromState,
  getWebSocketFromState,
  getDeploymentDetailByCardIdFromState,
  getCompanyFromState,
  getViewFromState,
  getViewGroupIdsFromState,
  getViewGroupIdsForGroupTypeAndColumnFromState,
  getViewGroupsIdsForGroupTypeFromState,
  getCardByCardContainerAndSlotNoFromState,
} from "features/planner/planner.selectors";

import { toCard } from "utils/route.util";
import config from "infrastructure/config";
import authService from "services/auth.service";
import cardService from "services/card.service";
import dataService from "services/data.service";
import userService from "services/user.service";
import viewService from "services/view.service";
import { getAllAppRoles } from "app/app.actions";
import groupService from "services/group.service";
import markerService from "services/marker.service";

export const clearCards = () => dispatch =>
  dispatch({ type: ACTIONS.CLEAR_CARDS });

/**
 * Company Group Relation
 */
export const createCompanyGroupRelation = makeActionCreator({
  type: ACTIONS.CREATE_COMPANY_GROUP_RELATION,
  payload: groupService.createCompanyGroupRelation,
});

export const getCompanyGroupRelations = makeActionCreator({
  type: ACTIONS.GET_COMPANY_GROUP_RELATIONS,
  payload: groupService.getCompanyGroupRelations,
});

/**
 * User
 */
export const getUsers = makeActionCreator({
  type: APP_ACTIONS.GET_USERS,
  payload: userService.getUsers,
});

export const updateUser = makeActionCreator({
  type: APP_ACTIONS.UPDATE_USER,
  payload: userService.updateUser,
});

/**
 * View
 */
export const getViews = makeActionCreator({
  type: ACTIONS.GET_VIEWS,
  payload: viewService.getViews,
});

export const getUserDefaultViewId = makeActionCreator({
  type: ACTIONS.GET_USER_DEFAULT_VIEW_ID,
  payload: viewService.getUserDefaultView,
});

export const getViewCompetencyStatuses = makeActionCreator({
  type: ACTIONS.GET_VIEW_COMPETENCY_STATUSES,
  payload: viewService.getViewCompetencyStatuses,
});

export const getView = (viewId, options) => dispatch => {
  Promise.all([
    dispatch(getSimpleView(viewId, options)),
    dispatch(getViewGroupProjects(viewId, options)),
  ]);
};

const getViewGroupProjects = (viewId, options) => dispatch => {
  const payload = viewService.getViewProjects(viewId);

  dispatch({
    payload,
    type: ACTIONS.GET_VIEW_GROUP_PROJECTS,
    ...options,
  });

  return payload;
};

export const getGroupProjects = (groupId, options) => dispatch => {
  const payload = viewService.getGroupProjects(groupId);

  dispatch({
    payload,
    type: ACTIONS.GET_GROUP_PROJECTS,
    ...options,
  });

  return payload;
};

const getSimpleView = (viewId, options) => dispatch => {
  const payload = viewService.getView(viewId);

  dispatch({
    payload,
    type: ACTIONS.GET_VIEW,
    ...options,
  });

  return payload;
};

export const viewOnLoad = viewId => dispatch =>
  Promise.all([
    dispatch(getView(viewId)),
    dispatch(openWebSocket(viewId)),
    dispatch(getViewCompetencyStatuses(viewId)),
  ]);

export const setUserDefaultView = makeActionCreator({
  type: ACTIONS.SET_USER_DEFAULT_VIEW_ID,
  payload: viewService.setUserDefaultViewId,
});

export const createView = makeActionCreator({
  type: ACTIONS.CREATE_VIEW,
  payload: viewService.createView,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "A new view was successfully created",
  },
});

export const updateView = makeActionCreator({
  type: ACTIONS.UPDATE_VIEW,
  payload: viewService.updateView,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "View successfully updated",
  },
});

export const deleteView = viewId => dispatch => {
  dispatch({
    viewId,
    type: ACTIONS.DELETE_VIEW,
    payload: () => viewService.deleteView(viewId),
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "View successfully deleted",
    },
    confirmationDialog: {
      title: "Delete view",
      confirmButtonText: "Delete",
      description: "Are you sure you want to delete this view?",
    },
  });
};

/**
 * ViewGroup
 */
export const createViewGroups = makeActionCreator(
  {
    type: ACTIONS.CREATE_VIEW_GROUPS,
    payload: viewService.createViewGroups,
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "Groups successfully added",
    },
  },
  "viewId",
);

export const updateViewGroup = makeActionCreator(
  {
    type: ACTIONS.UPDATE_VIEW_GROUP,
    payload: viewService.updateViewGroup,
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "Group successfully updated",
    },
  },
  "viewId",
  "viewGroupId",
  "viewGroup",
);

export const toggleViewGroupVisualState = (viewId, viewGroupId) => (
  dispatch,
  getState,
) => {
  var viewGroup = getViewGroupFromState(getState(), viewGroupId);

  return dispatch(
    updateViewGroup(
      viewId,
      viewGroupId,
      viewGroup.update("groupVisualState", groupVisualState =>
        groupVisualState === GROUP_VISUAL_STATE.NORMAL
          ? GROUP_VISUAL_STATE.MINIMIZED
          : GROUP_VISUAL_STATE.NORMAL,
      ),
    ),
  );
};

export const getViewGroup = makeActionCreator({
  type: ACTIONS.GET_VIEW_GROUP,
  payload: viewService.getViewGroup,
});

/**
 * Field
 */
export const getFields = makeActionCreator({
  type: ACTIONS.GET_FIELDS,
  payload: dataService.getFields,
});

export const createField = makeActionCreator({
  type: ACTIONS.CREATE_FIELD,
  payload: dataService.createField,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "Field successfully created",
  },
});

export const updateField = makeActionCreator({
  type: ACTIONS.UPDATE_FIELD,
  payload: dataService.updateField,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "Field successfully updated",
  },
});

export const deleteField = field => dispatch => {
  if (!field.get("sourceUri")) {
    dispatch({
      fieldId: field.get("id"),
      type: ACTIONS.DELETE_FIELD,
      payload: () => dataService.deleteField(field.get("id")),
      notification: {
        [NOTIFICATION_VARIANTS.SUCCESS]: "Field successfully deleted",
      },
      confirmationDialog: {
        title: "Delete field",
        description: `Are you sure you want to delete ${field.get("name")}?`,
        confirmButtonText: "Delete",
      },
    });
  } else {
    dispatch(
      addNotification({
        message: `Field ${field.get(
          "name",
        )} is read-only and cannot be removed.`,
        variant: NOTIFICATION_VARIANTS.ERROR,
      }),
    );
  }
};

/**
 * Country
 */
export const getCountries = makeActionCreator({
  type: ACTIONS.GET_COUNTRIES,
  payload: dataService.getCountries,
});

/**
 * Crew
 */
export const createCrew = makeActionCreator({
  type: ACTIONS.CREATE_CREW,
  payload: dataService.createCrew,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "Crew successfully created",
  },
});

export const deleteCrew = crew => dispatch => {
  dispatch({
    crewId: crew.get("id"),
    type: ACTIONS.DELETE_CREW,
    payload: () => dataService.deleteCrew(crew.get("id")),
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "Crew successfully deleted",
    },
    confirmationDialog: {
      title: "Delete crew",
      confirmButtonText: "Delete",
      description: `Are you sure you want to delete ${crew.get("name")}?`,
    },
  });
};

export const getCrews = makeActionCreator({
  type: ACTIONS.GET_CREWS,
  payload: dataService.getCrews,
});

export const updateCrew = makeActionCreator({
  type: ACTIONS.UPDATE_CREW,
  payload: dataService.updateCrew,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "Crew successfully updated",
  },
});

/**
 * Qualification
 */
export const getQualifications = makeActionCreator({
  type: ACTIONS.GET_QUALIFICATIONS,
  payload: dataService.getQualifications,
});

export const createQualification = makeActionCreator({
  type: ACTIONS.CREATE_QUALIFICATION,
  payload: dataService.createQualification,
});

export const getQualification = makeActionCreator({
  type: ACTIONS.GET_QUALIFICATION,
  payload: dataService.getQualification,
});

export const updateQualification = makeActionCreator({
  type: ACTIONS.UPDATE_QUALIFICATION,
  payload: dataService.updateQualification,
});

export const deleteQualification = qualification => dispatch => {
  dispatch({
    qualificationId: qualification.get("id"),
    type: ACTIONS.DELETE_QUALIFICATION,
    payload: () => dataService.deleteQualification(qualification.get("id")),
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "Qualification successfully deleted",
    },
    confirmationDialog: {
      confirmButtonText: "Delete",
      title: "Delete qualification",
      description: `Are you sure you want to delete ${qualification.get(
        "name",
      )}?`,
    },
  });
};

/**
 * Group
 */
export const getGroups = makeActionCreator({
  type: ACTIONS.GET_GROUPS,
  payload: groupService.getGroups,
});

export const getCardsForGroups = groupIds => dispatch => {
  const payload = cardService.getCardsForGroups(groupIds);

  dispatch({
    groupIds,
    payload,
    type: ACTIONS.GET_GROUP_CARDS,
  });

  return payload;
};

export const getGroupCardsCompetencyStatus = groupId => (
  dispatch,
  getState,
) => {
  const viewGroup = getViewGroupByGroupIdFromState(getState(), groupId);

  const payload =
    viewGroup && viewGroup.get("groupVisualState") === GROUP_VISUAL_STATE.NORMAL
      ? groupService.getGroupCardsCompetencyStatus(groupId)
      : Promise.resolve();

  dispatch({
    groupId,
    payload,
    type: ACTIONS.GET_GROUP_CARDS_SUMMARIZED_COMPETENCY_STATUS,
  });

  return payload;
};

export const createGroup = makeActionCreator({
  type: ACTIONS.CREATE_GROUP,
  payload: groupService.createGroup,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "Group successfully created",
  },
});

export const updateGroup = makeActionCreator(
  {
    type: ACTIONS.EDIT_GROUP,
    payload: groupService.updateGroup,
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "Group updated successfully",
    },
  },
  "groupId",
  "group",
);

export const updateGroupFlagType = makeActionCreator(
  {
    type: ACTIONS.UPDATE_GROUP_FLAG_TYPE,
    payload: debounce(groupService.updateGroupFlagType, 750),
  },
  "id",
  "flagType",
);

export const getGroup = makeActionCreator({
  type: ACTIONS.GET_GROUP,
  payload: groupService.getGroup,
});

export const getReplaceableEmployees = makeActionCreator(
  {
    type: ACTIONS.GET_REPLACEABLE_EMPLOYEES,
    payload: groupService.getReplacableEmployees,
  },
  "groupId",
);

export const groupExists = makeActionCreator({
  type: ACTIONS.GROUP_EXISTS,
  payload: groupService.groupExists,
});

export const undeleteGroup = makeActionCreator({
  type: ACTIONS.GROUP_UNDELETE,
  payload: groupService.undeleteGroup,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "Group successfully restored",
  },
});

/**
 * Facility
 */
export const getStationaryFacilities = makeActionCreator({
  type: ACTIONS.GET_STATIONARY_FACILITIES,
  payload: dataService.getFacilities,
});

export const getMobileFacilities = makeActionCreator({
  type: ACTIONS.GET_MOBILE_FACILITIES,
  payload: dataService.getMobileFacilities,
});

export const createFixedFacility = makeActionCreator({
  type: ACTIONS.CREATE_FIXED_FACILITY,
  payload: dataService.createFacility,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "Facility successfully created",
  },
});

export const updateFixedFacility = makeActionCreator({
  type: ACTIONS.UPDATE_FIXED_FACILITY,
  payload: dataService.updateFacility,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "Facility successfully updated",
  },
});

export const deleteFixedFacility = facility => dispatch => {
  if (!facility.get("sourceUri")) {
    dispatch({
      fieldId: facility.get("fieldId"),
      facilityId: facility.get("id"),
      type: ACTIONS.DELETE_FIXED_FACILITY,
      payload: () =>
        dataService.deleteFacility(facility.get("fieldId"), facility.get("id")),
      notification: {
        [NOTIFICATION_VARIANTS.SUCCESS]: "Facility successfully deleted",
      },
      confirmationDialog: {
        title: "Delete facility",
        description: `Are you sure you want to delete ${facility.get("name")}?`,
        confirmButtonText: "Delete",
      },
    });
  } else {
    dispatch(
      addNotification({
        message: `Facility ${facility.get(
          "name",
        )} is read-only and cannot be removed.`,
        variant: NOTIFICATION_VARIANTS.ERROR,
      }),
    );
  }
};

export const createMobileFacility = makeActionCreator({
  type: ACTIONS.CREATE_MOBILE_FACILITY,
  payload: dataService.createMobileFacility,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "Facility successfully created",
  },
});

export const updateMobileFacility = makeActionCreator({
  type: ACTIONS.UPDATE_MOBILE_FACILITY,
  payload: dataService.updateMobileFacility,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "Facility successfully updated",
  },
});

export const deleteMobileFacility = mobileFacility => dispatch => {
  if (!mobileFacility.get("sourceUri")) {
    dispatch({
      type: ACTIONS.DELETE_MOBILE_FACILITY,
      mobileFacilityId: mobileFacility.get("id"),
      payload: () => dataService.deleteMobileFacility(mobileFacility.get("id")),
      notification: {
        [NOTIFICATION_VARIANTS.SUCCESS]: "Mobile facility successfully deleted",
      },
      confirmationDialog: {
        title: "Delete mobile facility",
        description: `Are you sure you want to delete ${mobileFacility.get(
          "name",
        )}?`,
        confirmButtonText: "Delete",
      },
    });
  } else {
    dispatch(
      addNotification({
        message: `Mobile facility ${mobileFacility.get(
          "name",
        )} is read-only and cannot be removed.`,
        variant: NOTIFICATION_VARIANTS.ERROR,
      }),
    );
  }
};

/**
 * Card
 */
export const getCard = makeActionCreator({
  type: ACTIONS.GET_CARD,
  payload: cardService.getCard,
});

export const createCard = makeActionCreator({
  type: ACTIONS.CREATE_CARD,
  payload: cardService.createCard,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "A new card was successfully created",
  },
});

export const updateDummyCard = makeActionCreator({
  type: ACTIONS.EDIT_CARD,
  payload: cardService.updateDummyCard,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "Card successfully updated",
  },
});

export const deleteDummyCard = makeActionCreator(
  {
    type: ACTIONS.DELETE_CARD,
    payload: cardService.deleteDummyCard,
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "Card successfully deleted",
    },
  },
  "cardId",
);

export const getCardAllDeployments = makeActionCreator(
  {
    type: ACTIONS.GET_CARD_ALL_DEPLOYMENTS,
    payload: cardService.getCardAllDeployments,
  },
  "cardId",
);

export const getCardOpenDeployments = makeActionCreator(
  {
    type: ACTIONS.GET_CARD_OPEN_DEPLOYMENTS,
    payload: cardService.getCardOpenDeployments,
  },
  "cardId",
);

export const updateCardOpenDeployment = makeActionCreator(
  {
    type: ACTIONS.UPDATE_CARD_OPEN_DEPLOYMENT,
    payload: cardService.updateCardOpenDeployment,
  },
  "cardId",
);

export const unloadCardDeployments = () => dispatch =>
  dispatch({
    type: ACTIONS.UNLOAD_CARD_DEPLOYMENTS,
  });

export const deleteDeploymentDetail = deploymentDetail => dispatch =>
  dispatch({
    cardId: deploymentDetail.get("cardId"),
    type: ACTIONS.DELETE_DEPLOYMENT_DETAIL,
    deploymentDetailId: deploymentDetail.get("deploymentDetailId"),
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "Deployment successfully deleted",
    },
    confirmationDialog: {
      confirmButtonText: "Delete",
      title: "Delete travel history",
      description: `Are you sure you want to delete this deployment?`,
    },
    payload: () =>
      cardService
        .deleteDeploymentDetail(
          deploymentDetail.get("cardId"),
          deploymentDetail.get("deploymentDetailId"),
        )
        .then(() =>
          dispatch(getCardAllDeployments(deploymentDetail.get("cardId"))),
        ),
  });

/**
 * Marker
 */
export const getMarkers = makeActionCreator({
  type: ACTIONS.GET_MARKERS,
  payload: markerService.getMarkers,
});

export const createMarker = makeActionCreator({
  type: ACTIONS.CREATE_MARKER,
  payload: markerService.createMarker,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "Marker successfully uploaded",
    [NOTIFICATION_VARIANTS.ERROR]: "Failed to upload marker",
  },
});

export const updateMarker = makeActionCreator({
  type: ACTIONS.UPDATE_MARKER,
  payload: markerService.updateMarker,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "Marker successfully updated",
  },
});

export const deleteMarker = marker => dispatch => {
  dispatch({
    markerId: marker.get("id"),
    type: ACTIONS.DELETE_MARKER,
    payload: () => markerService.deleteMarker(marker.get("id")),
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "Marker successfully deleted",
    },
    confirmationDialog: {
      title: "Delete marker",
      confirmButtonText: "Delete",
      description: `Are you sure you want to delete ${marker.get("name")}?`,
    },
  });
};

/**
 * CardMove
 */
export const getCardMoves = makeActionCreator(
  {
    type: ACTIONS.GET_CARD_MOVES,
    payload: cardService.getCardMoves,
  },
  "cardId",
);

/**
 * Company
 */
export const createCompany = makeActionCreator({
  type: ACTIONS.CREATE_COMPANY,
  payload: dataService.createCompany,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "Company successfully created",
  },
});

export const deleteCompany = company => dispatch => {
  dispatch({
    companyId: company.get("id"),
    type: ACTIONS.DELETE_COMPANY,
    payload: () => dataService.deleteCompany(company.get("id")),
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "Company successfully deleted",
    },
    confirmationDialog: {
      title: "Delete company",
      description: `Are you sure you want to delete ${company.get("name")}`,
      confirmButtonText: "Delete",
    },
  });
};

export const getCompany = makeActionCreator({
  type: ACTIONS.GET_COMPANY,
  payload: dataService.getCompany,
});

export const getCompanies = makeActionCreator({
  type: ACTIONS.GET_COMPANIES,
  payload: dataService.getCompanies,
});

export const updateCompany = makeActionCreator({
  type: ACTIONS.UPDATE_COMPANY,
  payload: dataService.updateCompany,
  notification: {
    [NOTIFICATION_VARIANTS.SUCCESS]: "Company successfully updated",
  },
});

/**
 * Other
 */

export const getStationaryAndMobileFacilities = makeActionCreator({
  type: ACTIONS.GET_STATIONARY_AND_MOBILE_FACILITIES,
  payload: () => dispatch =>
    Promise.all([
      dispatch(getStationaryFacilities()),
      dispatch(getMobileFacilities()),
    ]),
});

export const createCardOnSubmit = toggleModal => dispatch => card =>
  dispatch(createCard(card)).then(toggleModal);

export const createViewOnSubmit = toggleModal => dispatch => view =>
  dispatch(createView(view)).then(toggleModal);

export const updateCardFailed = (message = "Failed to drop card") => dispatch =>
  dispatch({
    error: true,
    type: ACTIONS.CREATE_CARD_MOVE_ERROR,
    notification: {
      [NOTIFICATION_VARIANTS.ERROR]: message,
    },
  });

export const initializeReturnOnshore = (card, cardMove) => (
  dispatch,
  getState,
) => {
  dispatch(getCardOpenDeployments(card.get("id"))).then(() => {
    const deploymentDetail = getDeploymentDetailByCardIdFromState(
      getState(),
      card.get("id"),
    );

    dispatch(
      initialize(CREATE_CARD_MOVE_FORM.ID, {
        ...cardMove,
        [CREATE_CARD_MOVE_FORM.DEPLOYMENT_DETAILS]: {
          [CREATE_CARD_MOVE_FORM.QUALIFICATION_ID]: card.getIn([
            "employee",
            "qualificationId",
          ]),
          [CREATE_CARD_MOVE_FORM.COMMENT]: deploymentDetail.get("comment"),
          [CREATE_CARD_MOVE_FORM.DEPARTURE]: {
            [CREATE_CARD_MOVE_FORM.HOUR]: 12,
            [CREATE_CARD_MOVE_FORM.MINUTE]: 0,
            [CREATE_CARD_MOVE_FORM.DATE]: moment(),
          },
        },
      }),
    );

    dispatch(toggleModal({ modalId: RETURN_ONSHORE_MODAL_ID }));
  });
};

export const initializeDeployOffshore = (card, cardMove) => dispatch =>
  Promise.all([
    dispatch(getCrews()),
    dispatch(getReplaceableEmployees(cardMove.targetGroupId)),
    dispatch(queryEmployeeWorkSchedule(card.get("id"), {}))
      .catch(err => err) // ignore error as it's not critical for the user to show suggested start/end dates
      .then(({ startDate, endDate }) => {
        const start = startDate ? moment(startDate) : startDate;
        const end = endDate ? moment(endDate) : endDate;

        dispatch(
          initialize(CREATE_CARD_MOVE_FORM.ID, {
            ...cardMove,
            [CREATE_CARD_MOVE_FORM.DEPLOYMENT_DETAILS]: {
              [CREATE_CARD_MOVE_FORM.QUALIFICATION_ID]: card.getIn([
                "employee",
                "qualificationId",
              ]),
              [CREATE_CARD_MOVE_FORM.SHIFT]: SHIFT_TYPES.DAY,
              [CREATE_CARD_MOVE_FORM.ARRIVAL]: {
                [CREATE_CARD_MOVE_FORM.HOUR]: 12,
                [CREATE_CARD_MOVE_FORM.MINUTE]: 0,
                [CREATE_CARD_MOVE_FORM.DATE]: start,
              },
              [CREATE_CARD_MOVE_FORM.DEPARTURE]: {
                [CREATE_CARD_MOVE_FORM.HOUR]: 12,
                [CREATE_CARD_MOVE_FORM.MINUTE]: 0,
                [CREATE_CARD_MOVE_FORM.DATE]: end,
              },
            },
          }),
        );
      }),
  ]).then(() => dispatch(toggleModal({ modalId: DEPLOY_OFFSHORE_MODAL_ID })));

export const initializeShuttleOffshore = (card, cardMove) => (
  dispatch,
  getState,
) =>
  Promise.all([
    dispatch(getCardOpenDeployments(card.get("id"))),
    dispatch(getReplaceableEmployees(cardMove.targetGroupId)),
  ]).then(() => {
    const deploymentDetail = getDeploymentDetailByCardIdFromState(
      getState(),
      card.get("id"),
    );
    const endDate = deploymentDetail.get("endDate");

    dispatch(
      initialize(CREATE_CARD_MOVE_FORM.ID, {
        ...cardMove,
        [CREATE_CARD_MOVE_FORM.DEPLOYMENT_DETAILS]: {
          [CREATE_CARD_MOVE_FORM.CREW_ID]: deploymentDetail.get("crewId"),
          [CREATE_CARD_MOVE_FORM.QUALIFICATION_ID]: deploymentDetail.get(
            "qualificationId",
          ),
          [CREATE_CARD_MOVE_FORM.SHIFT]: deploymentDetail.get("shiftType"),
          [CREATE_CARD_MOVE_FORM.REPLACE_EMPLOYEE_ID]: deploymentDetail.get(
            "replaceEmployeeId",
          ),
          [CREATE_CARD_MOVE_FORM.COMMENT]: deploymentDetail.get("comment"),
          [CREATE_CARD_MOVE_FORM.ARRIVAL]: {
            [CREATE_CARD_MOVE_FORM.DATE]: moment(),
            [CREATE_CARD_MOVE_FORM.HOUR]: 12,
            [CREATE_CARD_MOVE_FORM.MINUTE]: 0,
          },
          [CREATE_CARD_MOVE_FORM.DEPARTURE]: {
            [CREATE_CARD_MOVE_FORM.DATE]: endDate,
            [CREATE_CARD_MOVE_FORM.HOUR]: endDate.hour(),
            [CREATE_CARD_MOVE_FORM.MINUTE]: endDate.minute(),
          },
        },
      }),
    );
    dispatch(toggleModal({ modalId: SHUTTLE_OFFSHORE_MODAL_ID }));
  });

export const deployEmployeeOnSubmit = toggleModal => dispatch => cardMove => {
  return dispatch(deployEmployee(cardMove)).then(toggleModal);
};

export const returnEmployeeOnSubmit = toggleModal => dispatch => cardMove => {
  return dispatch(deployEmployee(cardMove)).then(toggleModal);
};

export const deployEmployee = cardMove => (dispatch, getState) => {
  // cardMove can potentially be both JS and Immutable
  const cardMoveJS = isImmutable(cardMove) ? cardMove.toJS() : cardMove;
  const { targetCardContainerId, targetSlotNo, targetGroupId } = cardMoveJS;
  const group = getViewGroupByGroupIdFromState(getState(), targetGroupId);

  const conflictingCard = getCardByCardContainerAndSlotNoFromState(
    targetCardContainerId,
    targetSlotNo,
  )(getState());

  const payload = cardService
    .createCardMove(cardMoveJS.cardId, cardMove)
    .then(result => {
      Promise.all([
        dispatch(getViewGroupProjects(group.get("viewId"))),
        dispatch({
          ...cardMoveJS,
          payload,
          type: ACTIONS.CREATE_CARD_MOVE,
          notification: {
            [NOTIFICATION_VARIANTS.ERROR]: "Failed to drop card",
            [NOTIFICATION_VARIANTS.SUCCESS]: "Card relocated successfully",
          },
          conflictingCardId: conflictingCard
            ? conflictingCard.get("id")
            : undefined,
        }),
      ]);
      return result;
    })
    .catch(err => {
      var message = "Failed to drop card";
      if (err?.response?.body?.errors?.length > 0) {
        message = err?.response?.body?.errors[0];
      }

      dispatch(
        addNotification({
          message: message,
          variant: NOTIFICATION_VARIANTS.ERROR,
        }),
      );

      return err;
    });

  return payload;
};

/**
 * Employee Course
 */
export const getEmployeeCourses = makeActionCreator(
  {
    type: ACTIONS.GET_EMPLOYEE_COURSES,
    payload: cardService.getEmployeeCourses,
  },
  "cardId",
);

export const clearEmployeeCourses = cardId => dispatch =>
  dispatch({
    type: ACTIONS.CLEAR_EMPLOYEE_COURSES,
    cardId,
  });

/**
 * Employee Group
 */
export const getEmployeeGroups = makeActionCreator({
  type: ACTIONS.GET_EMPLOYEE_GROUPS,
  payload: dataService.getEmployeeGroups,
});

export const createEmployeeGroup = makeActionCreator({
  type: ACTIONS.CREATE_EMPLOYEE_GROUP,
  payload: dataService.createEmployeeGroup,
});

export const updateEmployeeGroup = makeActionCreator({
  type: ACTIONS.UPDATE_EMPLOYEE_GROUP,
  payload: dataService.updateEmployeeGroup,
});

export const createEmployeeGroupOnSubmit = toggleModal => dispatch => employeeGroup =>
  dispatch(createEmployeeGroup(employeeGroup.toJS())).then(toggleModal);

export const initializeEditEmployeeGroup = (
  employeeGroup,
  toggleModal,
) => dispatch => {
  dispatch(initialize(EMPLOYEE_GROUP_FORM.ID, employeeGroup));
  toggleModal();
};

export const editEmployeeGroupOnSubmit = toggleModal => dispatch => employeeGroup =>
  dispatch(
    updateEmployeeGroup(employeeGroup.get("id"), employeeGroup.toJS()),
  ).then(toggleModal);

export const deleteEmployeeGroup = employeeGroup => dispatch => {
  dispatch({
    type: ACTIONS.DELETE_EMPLOYEE_GROUP,
    employeeGroupId: employeeGroup.get("id"),
    payload: () => dataService.deleteEmployeeGroup(employeeGroup.get("id")),
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "Employee Group successfully deleted",
    },
    confirmationDialog: {
      confirmButtonText: "Delete",
      title: "Delete Employee Group",
      description: `Are you sure you want to delete ${employeeGroup.get(
        "name",
      )}?`,
    },
  });
};

export const createCardMove = cardMove => (dispatch, getState) => {
  const state = getState();

  const {
    cardId,
    sourceGroupId,
    targetGroupId,
    targetCardContainerId,
    sourceCardContainerId,
  } = cardMove;

  const card = getCardFromState(state, cardId);

  if (card.get("type") === CARD_TYPES.EMPLOYEE) {
    const sourceCardContainer = getCardContainerFromState(
      state,
      sourceGroupId,
      sourceCardContainerId,
    );

    const targetCardContainer = getCardContainerFromState(
      state,
      targetGroupId,
      targetCardContainerId,
    );

    if (isAllocated(cardMove, targetCardContainer)) {
      return dispatch(tryAllocateEmployee(card, cardMove));
    }

    if (isDeploymentOffshore(sourceCardContainer, targetCardContainer)) {
      return dispatch(initializeDeployOffshore(card, cardMove));
    }

    if (isReturnOnshore(sourceCardContainer, targetCardContainer)) {
      return dispatch(initializeReturnOnshore(card, cardMove));
    }

    if (isShuttle(sourceCardContainer, targetCardContainer)) {
      return dispatch(initializeShuttleOffshore(card, cardMove));
    }
  }

  return dispatch(deployEmployee(cardMove));
};

export const initializeCreateCard = (cardContainerId, slotNo) => dispatch => {
  dispatch(
    initialize(CARD_FORM.ID, {
      [CARD_FORM.SLOT_NO]: slotNo,
      [CARD_FORM.CARD_CONTAINER_ID]: cardContainerId,
    }),
  );
  dispatch(toggleModal({ modalId: CREATE_CARD_MODAL_ID }));
};

export const createPlanningGroupOnSubmit = toggleModal => dispatch => planningGroup =>
  dispatch(createGroup(planningGroup.toJS())).then(toggleModal);

export const createLocationGroupOnSubmit = toggleModal => dispatch => group =>
  dispatch(createGroup(group.toJS())).then(toggleModal);

export const deleteViewGroup = viewGroupId => (dispatch, getState) => {
  const viewGroup = getViewGroupFromState(getState(), viewGroupId);
  const group = getGroupFromState(getState(), viewGroup.get("groupId"));

  const viewId = viewGroup.get("viewId");

  dispatch({
    viewId,
    viewGroup,
    viewGroupId,
    type: ACTIONS.DELETE_VIEW_GROUP,
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "Group successfully deleted",
    },
    payload: () => viewService.deleteViewGroup(viewId, viewGroupId),
    confirmationDialog: {
      title: "Remove group",
      confirmButtonText: "Remove",
      description: `Are you sure you want to remove ${group.get(
        "name",
      )} from this view?`,
    },
  });
};

export const initializeEditGroup = (group, toggleModal) => dispatch => {
  dispatch(initialize(GROUP_FORM.ID, group));
  toggleModal();
};

export const editLocationGroupOnSubmit = toggleModal => dispatch => group =>
  dispatch(updateGroup(group.get("id"), group.toJS())).then(toggleModal);

export const editPlanningGroupOnSubmit = toggleModal => dispatch => group =>
  dispatch(updateGroup(group.get("id"), group.toJS())).then(toggleModal);

export const initializeEditCard = cardId => (dispatch, getState) => {
  const card = getCardFromState(getState(), cardId).toJS();

  dispatch(toggleModal({ modalId: EDIT_CARD_MODAL_ID }));
  dispatch(initialize(CARD_FORM.ID, card));
};

export const editCardOnSubmit = toggleModal => dispatch => card =>
  dispatch(updateDummyCard(card.get("id"), card.toJS())).then(toggleModal);

export const onEnterCreateView = () => dispatch => {
  dispatch(
    initialize(CREATE_VIEW_FORM.ID, {
      [CREATE_VIEW_FORM.TYPE]: VIEW_TYPES.PRIVATE,
    }),
  );
};

export const plannerContainerOnLoad = () => dispatch => {
  const payload = Promise.all([
    dispatch(getViews()),
    dispatch(getMarkers()),
    dispatch(getQualifications()),
  ]);

  dispatch({
    type: ACTIONS.PLANNER_CONTAINER_LOADED,
    payload,
  });

  return payload;
};

export const settingsFieldsOnLoad = () => dispatch =>
  Promise.all([dispatch(getFields()), dispatch(getCountries())]);

export const createViewGroupsOnSubmit = toggleModal => dispatch => form => {
  const activeTab = form.get(CREATE_VIEW_GROUPS_FORM.ACTIVE_TAB);
  const selectedItems = form.get(CREATE_VIEW_GROUPS_FORM.SELECTED_ITEMS);
  const viewId = form.get(CREATE_VIEW_GROUPS_FORM.VIEW_ID);

  let payload;

  switch (activeTab) {
    default:
      payload = dispatch(createViewGroups(viewId, selectedItems));
  }

  return payload.then(toggleModal);
};

export const initializeCreateViewGroupsForm = viewId => dispatch =>
  dispatch(
    initialize(CREATE_VIEW_GROUPS_FORM.ID, {
      [CREATE_VIEW_GROUPS_FORM.VIEW_ID]: viewId,
      [CREATE_VIEW_GROUPS_FORM.SELECTED_ITEMS]: EMPTY_SET,
      [CREATE_VIEW_GROUPS_FORM.ACTIVE_TAB]:
        CREATE_VIEW_GROUPS_TABS.LOCATION_GROUP,
    }),
  );

export const createViewGroupsonEnter = viewId => dispatch => {
  dispatch(initializeCreateViewGroupsForm(viewId));

  return viewService.getAvailableGroupsByView(viewId);
};

export const deleteGroup = group => dispatch =>
  dispatch({
    groupId: group.get("id"),
    type: ACTIONS.DELETE_GROUP,
    payload: () => groupService.deleteGroup(group.get("id")),
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "Group deleted successfully ",
    },
    confirmationDialog: {
      title: "Delete group",
      description: `Are you sure you want to delete ${group.get("name")}?`,
      confirmButtonText: "Delete",
    },
  });

export const createFieldOnSubmit = toggleModal => dispatch => field =>
  dispatch(createField(field.toJS())).then(toggleModal);

export const initializeEditField = (field, toggleModal) => dispatch => {
  dispatch(initialize(FIELD_FORM.ID, field));
  toggleModal();
};

export const editFieldOnSubmit = toggleModal => dispatch => field =>
  dispatch(updateField(field.get("id"), field.toJS())).then(toggleModal);

export const createFixedFacilityOnSubmit = toggleModal => dispatch => facility =>
  dispatch(createFixedFacility(facility.get("fieldId"), facility.toJS())).then(
    toggleModal,
  );

export const initializeEditFixedFacility = (
  facility,
  toggleModal,
) => dispatch => {
  dispatch(initialize(EDIT_FIXED_FACILITY_FORM.ID, facility));
  toggleModal();
};

export const editFixedFacilityOnSubmit = toggleModal => dispatch => facility =>
  dispatch(
    updateFixedFacility(
      facility.get("fieldId"),
      facility.get("id"),
      facility.toJS(),
    ),
  ).then(toggleModal);

export const createMobileFacilityOnSubmit = toggleModal => dispatch => facility =>
  dispatch(createMobileFacility(facility.toJS())).then(toggleModal);

export const initializeEditMobileFacility = (
  facility,
  toggleModal,
) => dispatch => {
  dispatch(initialize(MOBILE_FACILITY_FORM.ID, facility));
  toggleModal();
};

export const editMobileFacilityOnSubmit = toggleModal => dispatch => facility =>
  dispatch(updateMobileFacility(facility.get("id"), facility.toJS())).then(
    toggleModal,
  );

export const createCompanyOnSubmit = toggleModal => dispatch => formValues => {
  const newCompany = formValues.toJS();

  return dispatch(createCompany(newCompany)).then(toggleModal);
};

export const initializeEditCompany = (company, toggleModal) => dispatch => {
  dispatch(initialize(COMPANY_FORM.ID, company));

  toggleModal();
};

export const initializeEditCompanyById = (companyId, toggleModal) => (
  dispatch,
  getState,
) => {
  const payload = Promise.all([
    dispatch(getCompany(companyId)),
    dispatch(getCountries()),
  ])
    .then(() => {
      const company = getCompanyFromState(getState(), companyId);

      dispatch(initialize(COMPANY_FORM.ID, company));
    })
    .then(toggleModal);

  dispatch({
    type: ACTIONS.INITIALIZE_EDIT_COMPANY,
    payload,
  });

  return payload;
};

export const editCompanyOnSubmit = toggleModal => dispatch => formValues => {
  const company = formValues.toJS();

  return dispatch(updateCompany(company.id, formValues.toJS())).then(
    toggleModal,
  );
};

export const createCrewOnSubmit = toggleModal => dispatch => crew =>
  dispatch(createCrew(crew.toJS())).then(toggleModal);

export const initializeEditCrew = (crew, toggleModal) => dispatch => {
  dispatch(initialize(CREW_FORM.ID, crew));
  toggleModal();
};

export const editCrewOnSubmit = toggleModal => dispatch => crew =>
  dispatch(updateCrew(crew.get("id"), crew.toJS())).then(toggleModal);

export const createQualificationOnSubmit = toggleModal => dispatch => qualification =>
  dispatch(createQualification(qualification.toJS())).then(toggleModal);

export const initializeEditQualification = (
  qualification,
  toggleModal,
) => dispatch => {
  dispatch(initialize(QUALIFICATION_FORM.ID, qualification));
  toggleModal();
};

export const editQualificationOnSubmit = toggleModal => dispatch => qualification =>
  dispatch(
    updateQualification(qualification.get("id"), qualification.toJS()),
  ).then(toggleModal);

export const initializeEditMarker = (marker, toggleModal) => dispatch => {
  dispatch(initialize(MARKER_FORM.ID, marker));
  toggleModal();
};

export const editMarkerOnSubmit = toggleModal => dispatch => marker =>
  dispatch(updateMarker(marker.get("id"), marker.toJS())).then(toggleModal);

export const createMarkerOnSubmit = toggleModal => dispatch => marker =>
  getFileBase64(marker.get(MARKER_FORM.FILE).first())
    .then(
      fileBase64 =>
        dispatch(
          createMarker({
            icon: fileBase64,
            name: marker.get(MARKER_FORM.NAME),
          }),
        ),
      toggleModal(),
    )
    .catch(err => {
      var message = "Failed to create Marker";
      if (err?.response?.body?.errors?.length > 0) {
        message = err?.response?.body?.errors[0];
      }

      dispatch(
        addNotification({
          message: message,
          variant: NOTIFICATION_VARIANTS.ERROR,
        }),
      );

      return err;
    });
//.then(toggleModal);

export const searchView = (viewId, searchTerm) => dispatch => {
  const payload = viewService
    .searchView(viewId, searchTerm)
    .then(viewSearchResult => {
      const { view, groups } = viewSearchResult;

      dispatch({
        payload: view,
        type: ACTIONS.GET_VIEW,
      });

      dispatch({
        payload: groups,
        type: ACTIONS.GET_GROUPS,
      });

      return viewSearchResult;
    });

  dispatch({
    payload,
    type: ACTIONS.SEARCH_VIEW,
  });

  return payload;
};

export const clearSearch = viewId => dispatch =>
  dispatch({
    type: ACTIONS.CLEAR_SEARCH,
    payload: () => dispatch(getView(viewId)),
  });

export const openWebSocket = viewId => dispatch => {
  const payload = authService.acquireTokenSilent([config.scope]).then(token => {
    const url = `api/realtime/websocket/${viewId}`;
    const api = new URL(config.apiRoot, window.location);
    const wsUrl = new URL(`${url}?access_token=${token}`, api);
    wsUrl.protocol = wsUrl.protocol.replace("http", "ws");

    const websocket = new WebSocket(wsUrl);
    const clientId = sessionStorage.getItem("wsClientId");

    websocket.onmessage = event =>
      dispatch(onWebSocketMessage(event, viewId, clientId));
    websocket.onclose = event => dispatch(onWebSocketClose(event, viewId));
    websocket.onerror = event => dispatch(onWebSocketError(viewId));

    return websocket;
  });

  dispatch({
    viewId,
    payload,
    type: ACTIONS.WEBSOCKET_OPEN,
  });

  return payload;
};

const onWebSocketMessage = (event, viewId, clientId) => (
  dispatch,
  getState,
) => {
  const msg = JSON.parse(event.data);
  if (msg.ClientId === clientId) {
    // Skip if changes are from current client
    return;
  }

  const groupIds = List(msg.Data);
  // a non-empty array indicates that one or more ViewGroups have changed
  if (groupIds.size) {
    const viewGroups = List(groupIds)
      .map(groupId => getViewGroupByGroupIdFromState(getState(), groupId))
      .filter(Boolean);

    return Promise.all([
      dispatch(getViewGroupProjects(viewId)),
      msg.UpdateCards === true ? dispatch(getView(viewId)) : Promise.resolve(),
      viewGroups.map(viewGroup =>
        Promise.all([
          dispatch(getViewGroup(viewGroup.get("viewId"), viewGroup.get("id"))),
          dispatch(getGroupCardsCompetencyStatus(viewGroup.get("groupId"))),
        ]),
      ),
    ]);
  }

  // an empty array indicates that the View has changed
  return Promise.all([
    dispatch(getView(viewId, { silent: true })),
    dispatch(getViewCompetencyStatuses(viewId)),
  ]);
};

const onWebSocketClose = (event, viewId) => (dispatch, getState) => {
  const state = getState();
  const isAbnormalClose = event.code === 1006;
  const webSocketDidError = getWebSocketDidErrorFromState(state, viewId);

  const displayErrorMessage = isAbnormalClose && webSocketDidError;

  dispatch({
    viewId,
    type: ACTIONS.WEBSOCKET_CLOSE,
    confirmationDialog: displayErrorMessage && {
      title: "Live Updates Disconnected",
      description: `The Live Updates connection to the server has been lost. The page will reload in 60 seconds.`,
      confirmButtonText: "Reload Now",
      onConfirm: () => window.location.reload(),
    },
  });

  if (isAbnormalClose) {
    if (!getWebSocketDidErrorFromState(state, viewId)) {
      // reopen the websocket connection immediately if a previously working connection was closed by the server
      dispatch(openWebSocket(viewId));
    } else {
      // try again in a minute if the user does not reload using the confirmation dialog
      setTimeout(() => window.location.reload(), 60000);
    }
  }
};

const onWebSocketError = viewId => dispatch => {
  dispatch({
    viewId,
    type: ACTIONS.WEBSOCKET_ERROR,
    payload: null,
    error: true,
    notification: {
      [NOTIFICATION_VARIANTS.ERROR]: "Live Updates disconnected",
    },
  });
};

export const closeWebSocket = viewId => (dispatch, getState) => {
  const webSocket = getWebSocketFromState(getState(), viewId);

  if (webSocket) {
    webSocket.close();
  }
};

export const sortViewGroup = sortMetaData => dispatch => {
  dispatch({
    type: ACTIONS.SORT_VIEW_GROUP,
    ...sortMetaData,
  });
};

export const setViewGroupColumn = data => dispatch => {
  dispatch({
    type: ACTIONS.CHANGE_VIEW_GROUP_COLUMN,
    ...data,
  });
};

export const saveViewGroupOrder = viewId => (dispatch, getState) => {
  const state = getState();
  const currentView = getViewFromState(state, viewId);

  // get the ordered viewGroupIds
  const viewGroupIdsByTypeAndColumn = getViewGroupIdsFromState(state);

  // map to viewGroup and set correct viewColumn and viewColumn order based on their indices
  const viewGroups = viewGroupIdsByTypeAndColumn
    .map(viewGroupsByColumn =>
      viewGroupsByColumn.map((viewGroupIds, viewColumn) =>
        viewGroupIds.map((viewGroupId, viewColumnOrder) => {
          const viewGroup = getViewGroupFromState(
            state,
            viewGroupId.toString(),
          );

          return viewGroup
            .set("viewColumn", viewColumn)
            .set("viewColumnOrder", viewColumnOrder);
        }),
      ),
    )
    .toList()
    .flatten(2);

  const payload = viewService.updateViewGroupOrder(
    viewId,
    currentView.set("viewGroups", viewGroups),
  );

  dispatch({
    payload,
    type: ACTIONS.UPDATE_VIEW_GROUPS_ORDER,
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "View successfully updated",
    },
  });

  return payload;
};

const displayDeleteConfirmation = (
  viewGroupIds = EMPTY_LIST,
  viewGroupIdsByGroup = EMPTY_MAP,
) =>
  viewGroupIds.size === viewGroupIdsByGroup.flatten().size &&
  viewGroupIdsByGroup.size === 1;

export const deleteViewGroupColumn = (
  viewId,
  viewGroupType,
  viewColumn,
  callback,
) => (dispatch, getState) => {
  const viewGroupIds = getViewGroupIdsForGroupTypeAndColumnFromState(
    getState(),
    viewGroupType,
    viewColumn,
  );

  const viewGroupIdsByGroup = getViewGroupsIdsForGroupTypeFromState(
    getState(),
    viewGroupType,
  );

  const shouldDisplayDeleteConfirmation = displayDeleteConfirmation(
    viewGroupIds,
    viewGroupIdsByGroup,
  );

  const payload = dispatch({
    viewId,
    viewColumn,
    viewGroupType,
    type: ACTIONS.DELETE_VIEW_COLUMN,
    confirmationDialog: shouldDisplayDeleteConfirmation && {
      onConfirm: callback,
      title: "Remove column",
      confirmButtonText: "Remove",
      description: "This will remove any groups in this column. Are you sure?",
    },
  });

  if (!shouldDisplayDeleteConfirmation) {
    invokeIfFunction(callback);
  }

  return payload;
};

export const addViewGroupColumn = (
  viewId,
  viewGroupType,
  callback,
) => dispatch => {
  dispatch({
    viewId,
    viewGroupType,
    type: ACTIONS.ADD_VIEW_COLUMN,
  });

  invokeIfFunction(callback);
};

export const settingsCompanyOnLoad = () => dispatch =>
  dispatch({
    type: ACTIONS.SETTINGS_COMPANY_ON_LOAD,
    payload: () =>
      Promise.all([dispatch(getCompanies()), dispatch(getCountries())]),
  });

export const initializeEditLocationGroup = (group, toggleModal) => dispatch => {
  dispatch(initialize(GROUP_FORM.ID, group));
  toggleModal();
};

export const addEmployeeCardsToPlanningGroup = makeActionCreator({
  type: ACTIONS.ADD_EMPLOYEES_TO_GROUP,
  payload: groupService.addEmployeeCardsToPlanningGroup,
});

const resetFacilityField = () => dispatch =>
  dispatch({
    type: ACTIONS.RESET_GROUP_FORM_FACILITY,
  });

export const asyncValidateLocationGroup = (group, dispatch, { toggleModal }) =>
  dispatch(groupExists(group.toJS())).then(
    ({ exists, groupId, isDeleted, groupName }) => {
      if (exists && isDeleted) {
        dispatch({
          type: ACTIONS.GROUP_UNDELETE,
          payload: () => dispatch(undeleteGroup(groupId)),
          confirmationDialog: {
            onConfirm: toggleModal,
            confirmButtonText: "Restore",
            title: `Restore "${groupName}"`,
            description: `A location group already exists for "${groupName}", but it has been deleted. Do you want to restore it?`,
          },
        });

        dispatch(resetFacilityField());
      }
      return;
    },
  );

export const userAccessOnLoad = () => dispatch =>
  Promise.all([dispatch(getUsers()), dispatch(getAllAppRoles())]);

export const userAccessOnUnload = () => dispatch =>
  dispatch({
    type: ACTIONS.CLEAR_USERS,
  });

export const getCardWorkDays = makeActionCreator(
  {
    type: ACTIONS.GET_WORK_DAYS_BY_EMPLOYEE,
    payload: cardService.getCardWorkDays,
  },
  "employeeCardId",
);

/**
 * Sub Contractors
 */
export const getSubContractors = makeActionCreator({
  type: ACTIONS.GET_SUB_CONTRACTORS,
  payload: dataService.getSubContractors,
});

export const getSubContractor = makeActionCreator({
  type: ACTIONS.GET_SUB_CONTRACTOR,
  payload: dataService.getSubContractor,
});

export const createSubContractor = makeActionCreator({
  type: ACTIONS.CREATE_SUB_CONTRACTOR,
  payload: dataService.createSubContractor,
});

export const updateSubContractor = makeActionCreator({
  type: ACTIONS.UPDATE_SUB_CONTRACTOR,
  payload: dataService.updateSubContractor,
});

export const deleteSubContractor = card => dispatch => {
  dispatch({
    cardId: card.get("id"),
    employeeId: card.get("employeeId"),
    type: ACTIONS.DELETE_SUB_CONTRACTOR,
    payload: () => dataService.deleteSubContractor(card.get("employeeId")),
    notification: {
      [NOTIFICATION_VARIANTS.SUCCESS]: "Sub Contractor successfully deleted",
    },
    confirmationDialog: {
      confirmButtonText: "Delete",
      title: "Delete Sub Contractor",
      description: `Are you sure you want to delete ${card.get("name")}?`,
    },
  });
};

export const createSubContractorOnSubmit = toggleModal => dispatch => subContractor =>
  dispatch(
    createSubContractor(
      subContractor
        .set(SUB_CONTRACTOR_FORM.CARD_TYPE, CREATE_CARD_TYPE.SUB_CONTRACTOR)
        .toJS(),
    ),
  ).then(toggleModal);

export const editSubContractorOnSubmit = toggleModal => dispatch => subContractor =>
  dispatch(
    updateSubContractor(subContractor.get("id"), subContractor.toJS()),
  ).then(toggleModal);

const initializeSubContractorDetailsForm = employee => dispatch =>
  dispatch(initialize(SUB_CONTRACTOR_FORM.ID, employee));

export const subContractorDetailsOnLoad = subContractorCard => dispatch => {
  dispatch(
    initializeSubContractorDetailsForm(subContractorCard.get("employee")),
  );

  return Promise.all([
    dispatch(getCrews()),
    dispatch(getMarkers()),
    dispatch(getQualifications()),
    dispatch(getEmployeeGroups()),
  ]);
};

export const updateSubContractorFormOnChange = (values, dispatch, props) => {
  const { subContractor, dirty, employeeCardId } = props;

  if (dirty) {
    dispatch(updateEmployee(subContractor.get("id"), values.toJS()))
      .then(() => dispatch(getSubContractor(employeeCardId)))
      .then(() => dispatch(initializeSubContractorDetailsForm(values))); // make sure the form is dirty if you update the form back to intitial state
  }
};

const initializeSubContractorCompanyForm = companyId => (dispatch, getState) =>
  dispatch(
    initialize(
      SUB_CONTRACTOR_COMPANY_FORM.ID,
      CompanyMapper.from(getCompanyFromState(getState(), companyId)),
    ),
  );

export const subContractorCompanyOnLoad = companyId => dispatch =>
  dispatch(getCompanies()).then(() =>
    dispatch(initializeSubContractorCompanyForm(companyId)),
  );

export const updateSubContractorCompanyFormOnChange = (
  companyFormValues,
  dispatch,
  { dirty, subContractor },
) => {
  if (!dirty) return;

  subContractor = subContractor.setIn(
    ["employee", "companyId"],
    companyFormValues.get("id"),
  );

  dispatch(
    updateEmployee(
      subContractor.get("id"),
      subContractor.get("employee").toJS(),
    ),
  ).then(() => {
    dispatch(initializeSubContractorCompanyForm(companyFormValues.get("id")));
  });
};

export const openEmployeeCard = (
  card,
  subpage,
  navigateCurrentWindow,
) => dispatch => {
  const { availWidth, availHeight } = window.screen;
  const url = toCard(card, subpage);

  if (window.outerWidth > availWidth || window.outerHeight > availHeight) {
    openInWindow({
      to: `${url}${EMPLOYEE_CARD_HASH_TARGET}`,
    });
  } else if (navigateCurrentWindow) {
    dispatch(push(url));
  }
};

export const toggleDeploymentDetailsModal = deployment => dispatch => {
  dispatch(toggleModal({ modalId: DEPLOYMENT_DETAILS_MODAL_ID }));

  dispatch(
    initialize(
      DEPLOYMENT_DETAILS_FORM.ID,
      deployment.update(DEPLOYMENT_DETAILS_FORM.SHIFT, shiftType =>
        shiftTypeToString(shiftType),
      ),
    ),
  );
};

export const clearView = () => dispatch =>
  dispatch({
    type: ACTIONS.CLEAR_VIEW,
  });

export const getCardSpecificationGapReportForGroup = makeActionCreator(
  {
    type: ACTIONS.GET_CARD_MISSING_COMPETENCY_STATUS_ITEMS_FOR_GROUP,
    payload: cardService.getCardSpecificationGapReportForGroup,
  },
  "groupId",
  "cardId",
);

export const getCriticalMissingCardSpecificationGapReportForGroup = makeActionCreator(
  {
    type: ACTIONS.GET_CARD_MISSING_COMPETENCY_STATUS_ITEMS_FOR_GROUP,
    payload: cardService.getCriticalMissingCardSpecificationGapReportForGroup,
  },
  "groupId",
  "cardId",
);

const initializeMissingCompetenceModal = cardMove => dispatch => {
  dispatch(initialize(CREATE_CARD_MOVE_FORM.ID, cardMove));
  dispatch(toggleModal({ modalId: MISSING_COMPETENCE_MODAL_ID }));
};

export const tryAllocateEmployee = (card, cardMove) => dispatch =>
  dispatch(
    getCriticalMissingCardSpecificationGapReportForGroup(
      cardMove.targetGroupId,
      card.get("id"),
    ),
  ).then(({ specificationCompetencyStatuses }) => {
    if (
      specificationCompetencyStatuses &&
      specificationCompetencyStatuses.length
    ) {
      dispatch(initializeMissingCompetenceModal(cardMove));
    } else {
      return dispatch(deployEmployee(fromJS(cardMove)));
    }
  });

export const displayCardSpecificationGapReport = (
  cardId,
  groupId,
) => dispatch =>
  dispatch(getCardSpecificationGapReportForGroup(groupId, cardId)).then(() => {
    dispatch(
      initializeMissingCompetenceModal({
        cardId,
        targetGroupId: groupId,
      }),
    );
  });

export const allocateEmployeeOnSubmit = toggleModal => dispatch => cardMove => {
  return dispatch(deployEmployee(cardMove)).then(toggleModal);
};
